import React, { Children } from 'react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Shield, Cookie, Lock, Eye } from 'lucide-react';
import { FadeIn } from '../components/ui/FadeIn';
export function PrivacyPolicyPage() {
  const lastUpdated = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return <>
      <SEO title="Privacy Policy" description="Privacy Policy and Data Protection information for KMA UK. Learn how we protect your personal information." data-id="element-560" />

      <div className="bg-gray-50 py-16" data-id="element-561">
        <div className="container mx-auto px-4" data-id="element-562">
          <FadeIn className="max-w-4xl mx-auto text-center" data-id="element-563">
            <Shield className="h-12 w-12 mx-auto mb-6 text-primary-700" data-id="element-564" />
            <h1 className="text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-565">
              Privacy Policy
            </h1>
            <p className="text-gray-600" data-id="element-566">
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your personal information.
            </p>
            <p className="text-sm text-gray-500 mt-4" data-id="element-567">
              Last updated: {lastUpdated}
            </p>
          </FadeIn>
        </div>
      </div>

      <Section data-id="element-568">
        <div className="max-w-4xl mx-auto prose prose-lg" data-id="element-569">
          <FadeIn data-id="element-570">
            {/* Introduction */}
            <div className="mb-12" data-id="element-571">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-572">
                <Eye className="h-6 w-6 mr-3 text-primary-600" data-id="element-573" />
                Introduction
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-574">
                Khyber Medical Association UK ("we", "our", or "us") is
                committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your
                information when you visit our website, become a member, or
                interact with our services.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-575">
                We are registered as a charity in England and Wales and comply
                with UK data protection laws, including the General Data
                Protection Regulation (GDPR) and the Data Protection Act 2018.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12" data-id="element-576">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-577">
                <Lock className="h-6 w-6 mr-3 text-primary-600" data-id="element-578" />
                Information We Collect
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-579">
                We may collect personal information that you voluntarily provide
                to us when you:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2" data-id="element-580">
                <li data-id="element-581">Register for membership</li>
                <li data-id="element-582">Sign up for events or seminars</li>
                <li data-id="element-583">Make a donation</li>
                <li data-id="element-584">Contact us via email or contact form</li>
                <li data-id="element-585">Subscribe to our newsletter</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-586">
                This information may include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2" data-id="element-587">
                <li data-id="element-588">Name and contact details (email, phone, address)</li>
                <li data-id="element-589">Professional qualifications and GMC registration number</li>
                <li data-id="element-590">
                  Payment information (processed securely by third-party
                  providers)
                </li>
                <li data-id="element-591">Correspondence and communications with us</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12" data-id="element-592">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-593">
                How We Use Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-594">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2" data-id="element-595">
                <li data-id="element-596">Process your membership application and renewals</li>
                <li data-id="element-597">Communicate with you about events, news, and updates</li>
                <li data-id="element-598">Administer our charitable initiatives and programs</li>
                <li data-id="element-599">Process donations and issue receipts</li>
                <li data-id="element-600">Respond to your inquiries and provide support</li>
                <li data-id="element-601">Comply with legal obligations and maintain records</li>
                <li data-id="element-602">Improve our services and website functionality</li>
              </ul>
            </div>

            {/* Data Protection & Security */}
            <div className="mb-12" data-id="element-603">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-604">
                Data Protection & Security
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-605">
                We implement appropriate technical and organizational measures
                to protect your personal data against unauthorized access,
                alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4" data-id="element-606">
                <li data-id="element-607">Secure encrypted connections (SSL/TLS)</li>
                <li data-id="element-608">Regular security assessments and updates</li>
                <li data-id="element-609">Access controls and authentication</li>
                <li data-id="element-610">Secure payment processing through trusted providers</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-611">
                However, no method of transmission over the internet is 100%
                secure. While we strive to protect your information, we cannot
                guarantee absolute security.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-12" data-id="element-612">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-613">
                <Cookie className="h-6 w-6 mr-3 text-primary-600" data-id="element-614" />
                Cookie Policy
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-615">
                Our website uses cookies to improve your browsing experience.
                Cookies are small text files stored on your device that help us:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2" data-id="element-616">
                <li data-id="element-617">Remember your preferences and settings</li>
                <li data-id="element-618">Understand how you use our website</li>
                <li data-id="element-619">Improve website performance and functionality</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-620">
                You can control cookies through your browser settings. However,
                disabling cookies may affect your ability to use certain
                features of our website.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12" data-id="element-621">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-622">
                Your Rights
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-623">
                Under UK data protection law, you have the following rights:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2" data-id="element-624">
                <li data-id="element-625">
                  <strong data-id="element-626">Right to Access:</strong> Request a copy of the
                  personal data we hold about you
                </li>
                <li data-id="element-627">
                  <strong data-id="element-628">Right to Rectification:</strong> Request correction of
                  inaccurate or incomplete data
                </li>
                <li data-id="element-629">
                  <strong data-id="element-630">Right to Erasure:</strong> Request deletion of your
                  personal data (subject to legal obligations)
                </li>
                <li data-id="element-631">
                  <strong data-id="element-632">Right to Restrict Processing:</strong> Request
                  limitation on how we use your data
                </li>
                <li data-id="element-633">
                  <strong data-id="element-634">Right to Data Portability:</strong> Receive your data
                  in a structured, commonly used format
                </li>
                <li data-id="element-635">
                  <strong data-id="element-636">Right to Object:</strong> Object to processing of your
                  data for certain purposes
                </li>
                <li data-id="element-637">
                  <strong data-id="element-638">Right to Withdraw Consent:</strong> Withdraw consent
                  for data processing at any time
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-639">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:info@kmauk.org" className="text-primary-700 hover:underline" data-id="element-640">
                  info@kmauk.org
                </a>
              </p>
            </div>

            {/* Third-Party Services */}
            <div className="mb-12" data-id="element-641">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-642">
                Third-Party Services
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-643">
                We may use trusted third-party services to help us operate our
                website and deliver our services, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4" data-id="element-644">
                <li data-id="element-645">Payment processors (Stripe) for secure payment handling</li>
                <li data-id="element-646">Email service providers for communications</li>
                <li data-id="element-647">Website hosting and analytics services</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-648">
                These third parties are contractually obligated to protect your
                data and use it only for the purposes we specify.
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-12" data-id="element-649">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-650">
                Data Retention
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-651">
                We retain your personal data only for as long as necessary to
                fulfill the purposes outlined in this policy, comply with legal
                obligations, resolve disputes, and enforce our agreements.
                Membership data is typically retained for the duration of your
                membership plus 7 years for legal and accounting purposes.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12" data-id="element-652">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-653">
                Children's Privacy
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-654">
                Our services are not directed to individuals under the age of
                18. We do not knowingly collect personal information from
                children. If you believe we have collected information from a
                child, please contact us immediately.
              </p>
            </div>

            {/* Changes to This Policy */}
            <div className="mb-12" data-id="element-655">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-656">
                Changes to This Policy
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-657">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or legal requirements. We will notify
                you of any material changes by posting the new policy on this
                page and updating the "Last updated" date.
              </p>
            </div>

            {/* Contact Us */}
            <div className="mb-12 bg-primary-50 border-l-4 border-primary-600 p-6 rounded-r-lg" data-id="element-658">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-659">
                Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-660">
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-gray-700" data-id="element-661">
                <p data-id="element-662">
                  <strong data-id="element-663">Email:</strong>{' '}
                  <a href="mailto:info@kmauk.org" className="text-primary-700 hover:underline" data-id="element-664">
                    info@kmauk.org
                  </a>
                </p>
                <p data-id="element-665">
                  <strong data-id="element-666">Address:</strong> 17 Honeybourne, Bishop's Stortford,
                  CM23 4EF, United Kingdom
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-667">
                You also have the right to lodge a complaint with the
                Information Commissioner's Office (ICO), the UK's data
                protection authority, if you believe your data protection rights
                have been violated.
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>
    </>;
}